import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchSkillFamilies,
  fetchFamilyMembers,
  createSkillFamily,
  updateSkillFamily,
  addSkillToFamily,
  removeSkillFromFamily,
  fetchAllSkills,
  type SkillFamily,
  type RefItem,
} from '../../lib/referentialService';

const SkillFamilies: React.FC = () => {
  const [families, setFamilies] = useState<SkillFamily[]>([]);
  const [allSkills, setAllSkills] = useState<RefItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState<SkillFamily | null>(null);
  const [familyMembers, setFamilyMembers] = useState<RefItem[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFamilyCode, setNewFamilyCode] = useState('');
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newFamilyDesc, setNewFamilyDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const [searchSkill, setSearchSkill] = useState('');
  const [addingSkill, setAddingSkill] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [fams, skills] = await Promise.all([
          fetchSkillFamilies(),
          fetchAllSkills(),
        ]);
        setFamilies(fams);
        setAllSkills(skills);
      } catch (err) {
        console.error('Failed to load families', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectFamily = useCallback(async (family: SkillFamily) => {
    setSelectedFamily(family);
    setLoadingMembers(true);
    setSearchSkill('');
    try {
      const members = await fetchFamilyMembers(family.id);
      setFamilyMembers(members);
    } catch (err) {
      console.error('Failed to load family members', err);
      setFamilyMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  const handleCreateFamily = async () => {
    if (!newFamilyCode.trim() || !newFamilyName.trim()) return;
    setCreating(true);
    try {
      const created = await createSkillFamily({
        code: newFamilyCode,
        nameFr: newFamilyName,
        description: newFamilyDesc || undefined,
      });
      setFamilies((prev) => [...prev, created]);
      setShowCreateModal(false);
      setNewFamilyCode('');
      setNewFamilyName('');
      setNewFamilyDesc('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setCreating(false);
    }
  };

  const handleAddSkill = async (skillId: string) => {
    if (!selectedFamily) return;
    setAddingSkill(skillId);
    try {
      await addSkillToFamily(skillId, selectedFamily.id);
      const skill = allSkills.find((s) => s.id === skillId);
      if (skill) setFamilyMembers((prev) => [...prev, skill]);
      setFamilies((prev) => prev.map((f) =>
        f.id === selectedFamily.id ? { ...f, memberCount: (f.memberCount || 0) + 1 } : f
      ));
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setAddingSkill(null);
    }
  };

  const handleRemoveSkill = async (skillId: string) => {
    if (!selectedFamily) return;
    try {
      await removeSkillFromFamily(skillId, selectedFamily.id);
      setFamilyMembers((prev) => prev.filter((m) => m.id !== skillId));
      setFamilies((prev) => prev.map((f) =>
        f.id === selectedFamily.id ? { ...f, memberCount: Math.max(0, (f.memberCount || 0) - 1) } : f
      ));
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const memberIds = new Set(familyMembers.map((m) => m.id));
  const filteredAvailableSkills = allSkills.filter((s) =>
    !memberIds.has(s.id) &&
    (searchSkill.length < 2 || s.nameFr.toLowerCase().includes(searchSkill.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Familles transversales</h1>
          <p className="text-sm text-gray-500 mt-1">
            Regrouper des compétences de secteurs différents pour le matching cross-secteur
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors"
        >
          + Nouvelle famille
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Family list */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">{families.length} familles</h3>
          </div>
          <div className="divide-y divide-gray-50 max-h-[70vh] overflow-y-auto">
            {families.map((fam) => (
              <button
                key={fam.id}
                onClick={() => selectFamily(fam)}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                  selectedFamily?.id === fam.id ? 'bg-blue-50 border-l-2 border-[#00244F]' : ''
                }`}
              >
                <div className="text-sm font-medium text-gray-900">{fam.nameFr}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400 font-mono">{fam.code}</span>
                  <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded-full">
                    {fam.memberCount || 0} membres
                  </span>
                </div>
              </button>
            ))}
            {families.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">Aucune famille créée</div>
            )}
          </div>
        </div>

        {/* Family detail + members */}
        <div className="lg:col-span-2">
          {!selectedFamily ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="text-4xl mb-3">👈</div>
              <p className="text-sm text-gray-500">Sélectionner une famille pour gérer ses membres</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Members */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Membres de « {selectedFamily.nameFr} »
                  </h3>
                  <span className="text-xs text-gray-400">{familyMembers.length} compétences</span>
                </div>

                {loadingMembers ? (
                  <div className="p-8 text-center">
                    <div className="w-6 h-6 border-2 border-gray-200 border-t-[#00244F] rounded-full animate-spin mx-auto" />
                  </div>
                ) : (
                  <div className="p-4">
                    {familyMembers.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">Aucun membre</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {familyMembers.map((m) => (
                          <span key={m.id} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                            {m.nameFr}
                            <button
                              onClick={() => handleRemoveSkill(m.id)}
                              className="w-4 h-4 rounded-full bg-blue-200 hover:bg-red-300 text-blue-600 hover:text-red-700 flex items-center justify-center text-[10px] transition-colors"
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Add skill */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">Ajouter une compétence</h3>
                </div>
                <div className="p-4 space-y-3">
                  <input
                    type="text"
                    value={searchSkill}
                    onChange={(e) => setSearchSkill(e.target.value)}
                    placeholder="Rechercher une compétence..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                  />
                  {searchSkill.length >= 2 && (
                    <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                      {filteredAvailableSkills.slice(0, 20).map((sk) => (
                        <button
                          key={sk.id}
                          onClick={() => handleAddSkill(sk.id)}
                          disabled={addingSkill === sk.id}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 transition-colors flex items-center justify-between disabled:opacity-40"
                        >
                          <span className="text-gray-900">{sk.nameFr}</span>
                          <span className="text-xs text-green-600 font-medium">+ Ajouter</span>
                        </button>
                      ))}
                      {filteredAvailableSkills.length === 0 && (
                        <div className="px-3 py-4 text-center text-xs text-gray-400">Aucun résultat</div>
                      )}
                      {filteredAvailableSkills.length > 20 && (
                        <div className="px-3 py-2 text-center text-xs text-gray-400">
                          +{filteredAvailableSkills.length - 20} résultats non affichés
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Matching preview */}
              {familyMembers.length >= 2 && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-4">
                  <h4 className="text-xs font-semibold text-indigo-800 mb-2">Aperçu du matching</h4>
                  <p className="text-xs text-indigo-700">
                    Si un candidat a « {familyMembers[0]?.nameFr} », il verra aussi les offres demandant « {familyMembers[1]?.nameFr} »
                    {familyMembers.length > 2 && `, « ${familyMembers[2]?.nameFr} »`}
                    {familyMembers.length > 3 && ` et ${familyMembers.length - 3} autre(s)`}.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Nouvelle famille transversale</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={newFamilyCode}
                  onChange={(e) => setNewFamilyCode(e.target.value)}
                  placeholder="ex: accueil_client"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={newFamilyName}
                  onChange={(e) => setNewFamilyName(e.target.value)}
                  placeholder="ex: Accueil client"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newFamilyDesc}
                  onChange={(e) => setNewFamilyDesc(e.target.value)}
                  placeholder="Compétences liées à l'accueil et la relation client..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateFamily}
                disabled={!newFamilyCode.trim() || !newFamilyName.trim() || creating}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40"
              >
                {creating ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillFamilies;
